// Example usage:
//
// <div data-controller="dropdown">
//   <button data-action="click->dropdown#toggle">Toggle Dropdown</button>
//   <div data-dropdown-target="menu" class="hidden">
//     <!-- Dropdown menu items -->
//     <a href="#">Item 1</a>
//     <a href="#">Item 2</a>
//     <a href="#">Item 3</a>
//   </div>
// </div>


import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["menu"]

  connect() {
    // Add event listener to close dropdown when clicking outside
    document.addEventListener("click", this.closeDropdown.bind(this))
  }

  disconnect() {
    // Remove event listener when controller is disconnected
    document.removeEventListener("click", this.closeDropdown.bind(this))
  }

  toggle(event) {
    event.stopPropagation()
    const opening = this.menuTarget.classList.contains("hidden")

    // Only one menu open at a time. The outside-click listener cannot do this on
    // its own: toggle() stops the click propagating, so a click straight from one
    // row's trigger to another's never reaches document and the first menu stays
    // open behind the second.
    this.closeOtherMenus()

    this.menuTarget.classList.toggle("hidden")

    if (opening) {
      this.flipWhenClipped()
    }
  }

  // Hides every other dropdown's menu, resetting any flip it was holding so the
  // next open measures from the default downward position.
  closeOtherMenus() {
    document.querySelectorAll("[data-dropdown-target='menu']").forEach((menu) => {
      if (menu === this.menuTarget) return

      menu.classList.add("hidden")
      menu.style.top = ""
      menu.style.bottom = ""
      menu.style.marginTop = ""
      menu.style.marginBottom = ""
    })
  }

  // The menu can sit inside a scrolling container (the control room trip list),
  // where a row near the bottom edge would open the menu into the clipped area.
  // Open upwards instead when there is not enough room below and more room above.
  //
  // Positioned with inline styles rather than utility classes: this project is on
  // Tailwind 1, which has no bottom-full/top-full inset utilities.
  flipWhenClipped() {
    this.resetPosition()

    const bounds = this.clippingBounds()
    const anchor = this.element.getBoundingClientRect()
    const menuHeight = this.menuTarget.offsetHeight
    const spaceBelow = bounds.bottom - anchor.bottom
    const spaceAbove = anchor.top - bounds.top

    if (menuHeight > spaceBelow && spaceAbove > spaceBelow) {
      this.menuTarget.style.top = "auto"
      this.menuTarget.style.bottom = "100%"
      this.menuTarget.style.marginTop = "0"
      this.menuTarget.style.marginBottom = "0.5rem"
    }
  }

  // Back to the default downward position defined by the markup's mt-2.
  resetPosition() {
    this.menuTarget.style.top = ""
    this.menuTarget.style.bottom = ""
    this.menuTarget.style.marginTop = ""
    this.menuTarget.style.marginBottom = ""
  }

  // The nearest ancestor that clips overflow, falling back to the viewport when
  // the menu is not inside one.
  clippingBounds() {
    let element = this.element.parentElement

    while (element && element !== document.body) {
      const style = getComputedStyle(element)

      if (style.overflowY !== "visible" || style.overflowX !== "visible") {
        return element.getBoundingClientRect()
      }

      element = element.parentElement
    }

    return { top: 0, bottom: window.innerHeight }
  }

  closeDropdown(event) {
    if (!this.element.contains(event.target)) {
      this.menuTarget.classList.add("hidden")
    }
  }
}
